import {
  Actions,
  Async,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { useEffect, useState } from 'react';

import {
  ContentTab,
  ContentTree,
  Content,
  ContentType,
  ExtrasContent,
} from '../model/extras.domain';
import { useExtrasModel } from '../model/extras.model';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import {
  defaultValues,
  emptyTab,
  filterExtrasVM,
  filterTypeOptions,
  getContentName,
  getSlidesFinalOrder,
  toggleIsPublished,
  updateTreeItems,
} from './extras.mappers';
import { Draggable } from './extras.types';
import { useUser } from '@client/new/hooks/use-user';
import { mixTrack } from '@services/mixpanel';
import { ProfileData } from '@design-system/components/mobile-preview/types';
import toast from 'react-hot-toast';
import { FormMapType, useFormMap } from '@client/new/helpers/form/useFormMap';
import { v4 } from 'uuid';
import { selector } from '../model/tree';
import { addImage, removeImage } from './slides2.viewModel';

export type Filters = {
  type: (typeof filterTypeOptions)[number]['value'] | '';
};

export type ExtrasVMActionMap = {
  AddSlide: File;
  Create: ContentType;
  Edit: string;
  Delete: string;
  ToggleVisiblity: null;
  SaveTab: string;
  SetSearch: string;
  SetFilterType: Filters['type'];
  Reorder: Draggable[];
  ToggleItemVisible: string;
  CloseModal: null;
  CreateContent: Content;
  ConfirmDelete: null;
  Submit: null;
  ReorderSlides: string[];
  RemoveSlide: string;
  SaveSlides: string; // parentId
};

export type ExtrasVMAction = Actions<ExtrasVMActionMap>;

export type ExtrasVM = {
  search: string;
  filters: Filters;
  modalMode: 'create' | 'edit' | 'none' | 'delete';
  formType: ContentType;
  currentId: string;
  isExtrasEmpty: boolean;
  profile: ProfileData;
  upload: (file: File) => Promise<string>;
  savingSlides: Set<string>;
  tabs: Array<ContentTab['value']>;
  primaryColor: string;
} & Dispatchers<ExtrasVMActionMap> &
  ExtrasContent &
  FormMapType;

export const useExtrasVM = (): Async<ExtrasVM> => {
  const influencerId = useInfluencerId();
  const [localExtrasContent, setLocalExtrasContent] = useState<ExtrasContent>({
    contentIds: {
      id: 'empty',
    },
    contentMap: {},
    childrenMap: {},
  });

  const model = useExtrasModel({
    creatorId: influencerId,
  });
  const [search, setSearch] = useState<string>('');
  const [filters, setFilters] = useState<Filters>({
    type: '',
  });
  const { roleLite } = useUser();
  const [currentId, setCurrentId] = useState<string>('');
  const [modalModeType, setModalModeType] = useState<
    'create' | 'edit' | 'none' | 'delete'
  >('none');

  const { formMap, ...form } = useFormMap();

  const [formType, setFormType] = useState<ContentType>('ContentLink');
  const mainTab = localExtrasContent.contentIds?.items?.[0];
  const topLevelItems = mainTab?.items ?? [];
  const isExtrasEmpty = topLevelItems.length === 0;
  const canDeleteExtras = roleLite !== 'user';

  const handleSubmit = async (data: Content): Promise<unknown> => {
    if (model.data.type !== 'fetched')
      return Promise.reject(new Error('Model not fetched'));

    if (modalModeType === 'create') {
      const parentId = mainTab?.id || 'empty';

      await model.create(data, parentId, null);

      mixTrack({
        type: 'Extras Block Created',
        payload: {
          type: formType,
          title: getContentName(data),
        },
      });
      return;
    }

    if (modalModeType === 'edit' && currentId) {
      await model.update({
        type: data.type,
        value: { ...data.value, id: currentId },
      } as Content);

      mixTrack({
        type: 'Extras Block Saved',
        payload: { type: formType, title: getContentName(data) },
      });
      return;
    }
    return Promise.reject(new Error('Invalid modal mode or currentId'));
  };

  const [saving, setSaving] = useState<Set<string>>(new Set());

  const [pendingSlideOperations, setPendingSlideOperations] = useState<{
    [parentId: string]: {
      order: string[];
      toDelete: string[];
    };
  }>({});

  const saveSlidesBroken = async (parentId: string) => {
    if (model.data.type !== 'fetched') return;
    const children = localExtrasContent.childrenMap[parentId] || [];

    setSaving((prev) => new Set(prev).add(parentId));

    try {
      const createdSlideIds: string[] = [];

      // Create all slides in parallel
      const slideCreationPromises = children
        .filter((child) => {
          const formData = formMap[child.id]?.getValues();
          return formData?.file;
        })
        .map(async (child) => {
          const formData = formMap[child.id]?.getValues();
          await model.create(
            {
              type: 'ContentImage',
              value: {
                title: formData.title || child.id,
                id: child.id,
                isPublished: formData.isPublished || true,
              },
            } as Content,
            parentId,
            formData.file,
          );
          return child.id;
        });

      if (slideCreationPromises.length > 0) {
        const createdIds = await Promise.all(slideCreationPromises);
        createdSlideIds.push(...createdIds);
      }

      const pending = pendingSlideOperations[parentId];
      if (pending?.toDelete?.length > 0) {
        for (const slideId of pending.toDelete) {
          const slideContent = model.data.value.contentMap[slideId];
          if (slideContent) await model.delete(slideContent);
        }
      }

      if (pending?.order?.length === 0 && createdSlideIds.length === 0) {
        setPendingSlideOperations((prev) => {
          const { [parentId]: _removed, ...rest } = prev;
          return rest;
        });
        setModalModeType('none');
        setFormType('ContentLink');
        setCurrentId('');
        return;
      }

      const finalOrder = getSlidesFinalOrder({
        currentOrder: children,
        order: pending?.order || createdSlideIds,
        toDelete: pending?.toDelete || [],
      });

      setLocalExtrasContent((prev) => {
        const updatedContentIds = updateTreeItems(
          prev.contentIds,
          parentId,
          finalOrder.map((id) => ({ id })),
        );

        if (createdSlideIds?.length !== 1)
          model.reorder(updatedContentIds).then(() => {
            setPendingSlideOperations((prev) => {
              const { [parentId]: _removed, ...rest } = prev;
              return rest;
            });
          });

        setModalModeType('none');
        setFormType('ContentLink');
        setCurrentId('');

        return prev;
      });
    } catch (error) {
      toast.error('Failed to save slides');
      console.error('Slide save error:', error);
    } finally {
      setSaving((prev) => {
        const newSet = new Set(prev);
        newSet.delete(parentId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    if (model.data.type !== 'fetched') return;
    if (model.data.value.contentIds.id === 'empty') {
      return dispatchers.dispatch('Create', 'ContentTab');
    }
  }, [model.data.type]);

  useEffect(() => {
    if (model.data.type !== 'fetched') return;
    const contentIds = model.data.value.contentIds;
    const childrenMap = model.data.value.childrenMap;
    const filteredContentMap = filterExtrasVM(
      model.data.value,
      search,
      filters,
    );
    setLocalExtrasContent(() => ({
      contentIds,
      contentMap: filteredContentMap,
      childrenMap,
    }));
  }, [JSON.stringify(model.data), search, filters]);

  const _dispatch = (a: ExtrasVMAction) => {
    if (model.data.type !== 'fetched') return;
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'SaveTab': {
        if (modalModeType === 'create') {
          model
            .create(emptyTab(a.payload), model.data.value.contentIds.id, null)
            .then(() => {
              setModalModeType('none');
              setFormType('ContentLink');
            });
        }
        if (modalModeType === 'edit' && currentId) {
          const contentTab = model.data.value.contentMap[currentId];

          if (!contentTab || contentTab.type !== 'ContentTab') return;

          const updatedContent: ContentTab = {
            ...contentTab,
            value: {
              ...contentTab.value,
              name: a.payload,
            },
          };

          model.update(updatedContent).then(() => {
            setModalModeType('none');
            setFormType('ContentLink');
          });
        }

        mixTrack({
          type: 'Extra Tab Named and Saved',
          payload: {
            'Tab Name': a.payload,
          },
        });
        return;
      }
      case 'SetSearch': {
        setSearch(a.payload);
        return;
      }
      case 'SetFilterType': {
        const newFilters = { ...filters, type: a.payload };
        setFilters(newFilters);
        return;
      }
      case 'Create': {
        const id = v4();
        setModalModeType('create');
        setFormType(a.payload);
        setCurrentId(id);

        setLocalExtrasContent((prev) => ({
          ...prev,
          contentMap: {
            ...prev.contentMap,
            [id]: {
              type: a.payload,
              value: { ...defaultValues[a.payload], id },
            } as Content,
          },
        }));

        return;
      }
      case 'CloseModal': {
        setModalModeType('none');
        setFormType('ContentLink');
        setCurrentId('');
        form.reset();
        return;
      }
      case 'Edit': {
        const contentId = a.payload;
        const content = model.data.value.contentMap[contentId];
        if (!content) return;

        if (content.type === 'ContentLegacyGuide' && roleLite === 'user')
          return;

        setModalModeType('edit');
        setFormType(content.type);
        setCurrentId(contentId);

        return;
      }
      case 'ToggleItemVisible': {
        const contentId = a.payload;
        setCurrentId(contentId);
        const c = localExtrasContent.contentMap[contentId];

        const updatedContent = toggleIsPublished(c);

        model
          .update(updatedContent)
          .then(() => {
            mixTrack({
              type: 'Extras Block visibility toggled',
              payload: {
                type: c.type,
                title: getContentName(c),
                visibility: updatedContent.value.isPublished,
              },
            });
          })
          .catch(() => {
            toast.error('Failed to change visibility');
            setLocalExtrasContent((prev) => ({
              ...prev,
              contentMap: {
                ...prev.contentMap,
                [contentId]: c,
              },
            }));
          });

        setLocalExtrasContent((prev) => ({
          ...prev,
          contentMap: {
            ...prev.contentMap,
            [contentId]: toggleIsPublished(c),
          },
        }));

        return;
      }
      case 'Reorder': {
        setLocalExtrasContent((prev) => {
          const updatedContentIds = updateTreeItems(
            prev.contentIds,
            (prev.contentIds.items || [])[0].id,
            a.payload.map((item) => ({ id: item.id })),
          );

          model.reorder(updatedContentIds).catch(() => {
            toast.error('Reorder failed');
            setLocalExtrasContent((prev) => ({
              ...prev,
              contentIds: prev.contentIds,
            }));
          });

          return {
            ...prev,
            contentIds: updatedContentIds,
          };
        });
        return;
      }
      case 'Delete': {
        setCurrentId(a.payload);
        setModalModeType('delete');
        return;
      }

      case 'Submit': {
        if (!currentId) return;
        const data = formMap[currentId];
        const value = data.getValues();

        setSaving((prev) => new Set(prev).add(currentId));

        handleSubmit({
          type: formType,
          value,
        } as Content)
          .then(() => {
            if (formType === 'ContentSlides') {
              saveSlidesBroken(currentId);
            } else {
              setModalModeType('none');
              setFormType('ContentLink');
            }
          })
          .catch((error) => {
            console.error('Submit failed:', error);
            toast.error('Failed to save content');
          })
          .finally(() => {
            if (formType === 'ContentSlides') return;
            setSaving((prev) => {
              const newSet = new Set(prev);
              newSet.delete(currentId);
              return newSet;
            });
          });

        return;
      }

      case 'ConfirmDelete': {
        if (!currentId) return;
        const c = localExtrasContent.contentMap[currentId];
        model
          .delete(c)
          .then(() => {
            mixTrack({
              type: 'Extras Block Deleted',
              payload: {
                type: c.type,
                title: getContentName(c),
              },
            });
            setModalModeType('none');
            setCurrentId('');
          })
          .catch(() => {
            console.error('Delete failed');
          });

        return;
      }

      case 'ReorderSlides': {
        const newOrder = a.payload;

        setPendingSlideOperations((prev) => {
          const updated = {
            ...prev,
            [currentId]: {
              ...prev[currentId],
              order: newOrder,
            },
          };
          return updated;
        });

        setLocalExtrasContent((prev) => {
          const newChildrenMap = newOrder
            .map((id) =>
              prev.childrenMap[currentId]?.find((child) => child.id === id),
            )
            .filter((child): child is ContentTree => child !== undefined);

          return {
            ...prev,
            childrenMap: {
              ...prev.childrenMap,
              [currentId]: newChildrenMap,
            },
          };
        });

        return;
      }

      case 'RemoveSlide': {
        const slideId = a.payload;

        setPendingSlideOperations((prev) => ({
          ...prev,
          [currentId]: {
            order: prev[currentId]?.order || [],
            toDelete: [...(prev[currentId]?.toDelete || []), slideId],
          },
        }));

        setLocalExtrasContent(
          removeImage({ parentId: currentId, imageId: slideId }),
        );
        return;
      }

      case 'AddSlide': {
        if (saving.has(currentId)) {
          toast.custom('Please wait for current save to complete');
          return;
        }

        setLocalExtrasContent((prev) => {
          const next = addImage({
            file: a.payload,
            id: v4(),
            parentId: currentId,
          })(prev);
          return next;
        });
        return;
      }
    }
  };

  const dispatchers = getDispatchers<ExtrasVMActionMap>(_dispatch);

  if (
    model.data.type !== 'fetched' &&
    localExtrasContent.contentIds.id === 'empty'
  )
    return model.data;

  if (model.profile.type !== 'fetched') return model.profile;
  const tabs = selector(
    localExtrasContent.contentIds,
    localExtrasContent.contentMap,
    (v) => v.type === 'ContentTab',
  ).map((v) => v.value);
  const primaryColor = `#${
    model.profile.value.customColorSchema?.primaryColor ?? '000'
  }`;

  return toAsyncFetched({
    profile: model.profile.value,
    savingSlides: saving,
    isExtrasEmpty,
    canDeleteExtras,
    search,
    filters,
    formType,
    formMap,
    currentId,
    tabs,
    primaryColor,
    modalMode: modalModeType,
    upload: (file: File) => model.uploadFile(file),
    ...dispatchers,
    ...localExtrasContent,
    ...form,
  });
};
